import React, { useEffect, useState } from 'react';
import { X, Heart, Diamond, Lock, Play, Star } from 'lucide-react';
import { MaschaSkinsData } from './MaschaSkinsData';
interface TeasePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  skinId: string;
  userTokens: {
    loveTokens: number;
    desireGems: number;
  };
  onUnlock: (skinId: string) => void;
}
export const TeasePreviewModal: React.FC<TeasePreviewModalProps> = ({
  isOpen,
  onClose,
  skinId,
  userTokens,
  onUnlock
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUnlockPrompt, setShowUnlockPrompt] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  // Find the selected skin data
  const skinData = MaschaSkinsData.find(skin => skin.id === skinId);
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsPlaying(false);
      setShowUnlockPrompt(false);
      setPreviewProgress(0);
    }
  }, [isOpen, skinId]);
  // Handle preview progress
  useEffect(() => {
    if (!isPlaying || !isOpen) return;
    const interval = setInterval(() => {
      setPreviewProgress(prev => {
        const newProgress = prev + 1;
        if (newProgress >= 100) {
          setIsPlaying(false);
          setShowUnlockPrompt(true);
          clearInterval(interval);
        }
        return newProgress > 100 ? 100 : newProgress;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [isPlaying, isOpen]);
  if (!isOpen || !skinData) return null;
  const handlePlayPreview = () => {
    setIsPlaying(true);
    setPreviewProgress(0);
  };
  const handleUnlock = () => {
    onUnlock(skinId);
  };
  // Check if user has enough currency
  const hasEnoughCurrency = skinData.currency_type === 'Love Tokens' && userTokens.loveTokens >= skinData.price || skinData.currency_type === 'Desire Gems' && userTokens.desireGems >= skinData.price;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-21204">
      <div className="relative w-full max-w-2xl bg-gradient-to-b from-gray-900 to-black rounded-xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-amber-800/30" data-id="element-21205">
        <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" onClick={onClose} data-id="element-21206">
          <X size={18} data-id="element-21207" />
        </button>
        <div className="p-6" data-id="element-21208">
          {/* Header */}
          <div className="flex items-center justify-between mb-4" data-id="element-21209">
            <div className="flex items-center gap-3" data-id="element-21210">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-600 to-red-600 flex items-center justify-center" data-id="element-21211">
                <span className="text-white font-bold" data-id="element-21212">M</span>
              </div>
              <div data-id="element-21213">
                <h2 className="text-xl font-bold text-amber-400" data-id="element-21214">
                  {skinData.skin_name}
                </h2>
                <p className="text-gray-400 text-sm" data-id="element-21215">Tease Preview</p>
              </div>
            </div>
            <div className="px-2 py-1 bg-black/50 rounded-lg border border-amber-800/30 flex items-center" data-id="element-21216">
              {skinData.rarity === 'Legendary' && <div className="flex items-center" data-id="element-21217">
                  <Star size={14} className="text-amber-400 mr-1" fill="currentColor" data-id="element-21218" />
                  <Star size={14} className="text-amber-400 mr-1" fill="currentColor" data-id="element-21219" />
                  <Star size={14} className="text-amber-400" fill="currentColor" data-id="element-21220" />
                </div>}
              {skinData.rarity === 'Epic' && <div className="flex items-center" data-id="element-21221">
                  <Star size={14} className="text-purple-400 mr-1" fill="currentColor" data-id="element-21222" />
                  <Star size={14} className="text-purple-400" fill="currentColor" data-id="element-21223" />
                </div>}
              {skinData.rarity === 'Rare' && <div className="flex items-center" data-id="element-21224">
                  <Star size={14} className="text-blue-400" fill="currentColor" data-id="element-21225" />
                </div>}
            </div>
          </div>

          {/* Preview image */}
          <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden mb-4" data-id="element-21226">
            <img src={skinData.preview_image_url} alt={skinData.skin_name} className={`w-full h-full object-cover object-top ${isPlaying ? 'animate-subtle-pulse' : ''}`} data-id="element-21227" />
            {/* Play button overlay (only shown when not playing) */}
            {!isPlaying && !showUnlockPrompt && <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center cursor-pointer" onClick={handlePlayPreview} data-id="element-21228">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-600 to-red-600 flex items-center justify-center animate-pulse" data-id="element-21229">
                  <Play size={32} className="text-white ml-1" fill="currentColor" data-id="element-21230" />
                </div>
                <p className="text-white mt-3 font-medium" data-id="element-21231">Watch Preview</p>
              </div>}
            {/* Progress bar (only shown when playing) */}
            {isPlaying && <div className="absolute bottom-0 inset-x-0 h-1 bg-gray-800" data-id="element-21232">
                <div className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-100 ease-linear" style={{
              width: `${previewProgress}%`
            }} data-id="element-21233"></div>
              </div>}
            {/* Unlock prompt (shown after preview) */}
            {showUnlockPrompt && <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" data-id="element-21234">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-21235">
                  Unlock This Skin?
                </h3>
                <p className="text-gray-300 mb-4" data-id="element-21236">
                  Experience Mascha's full persona and reveal the next chapter
                  of her story.
                </p>
                <div className="flex items-center justify-center gap-2 mb-4" data-id="element-21237">
                  {skinData.currency_type === 'Love Tokens' ? <Heart size={18} className="text-pink-500" fill="currentColor" data-id="element-21238" /> : <Diamond size={18} className="text-blue-400" data-id="element-21239" />}
                  <span className="font-bold text-lg text-white" data-id="element-21240">
                    {skinData.price}
                  </span>
                  <span className="text-gray-300" data-id="element-21241">
                    {skinData.currency_type}
                  </span>
                </div>
                <button onClick={handleUnlock} disabled={!hasEnoughCurrency} className={`px-6 py-2 rounded-lg text-white font-medium ${hasEnoughCurrency ? 'bg-gradient-to-r from-amber-600 to-red-600 hover:shadow-lg hover:shadow-amber-500/20' : 'bg-gray-700 cursor-not-allowed'} transition-all`} data-id="element-21242">
                  {hasEnoughCurrency ? 'Unlock Now' : 'Insufficient Funds'}
                </button>
              </div>}
          </div>

          {/* Skin description */}
          <div className="bg-black/40 rounded-lg p-4 border border-amber-800/30 mb-4" data-id="element-21243">
            <h3 className="text-amber-400 font-medium mb-2" data-id="element-21244">
              Storyline Preview
            </h3>
            <p className="text-gray-300 text-sm italic" data-id="element-21245">
              "{skinData.storyline_piece.substring(0, 100)}..."
            </p>
          </div>

          {/* Price and unlock button */}
          <div className="flex items-center justify-between" data-id="element-21246">
            <div className="flex items-center" data-id="element-21247">
              {skinData.currency_type === 'Love Tokens' ? <Heart size={18} className="text-pink-500 mr-1" fill="currentColor" data-id="element-21248" /> : <Diamond size={18} className="text-blue-400 mr-1" data-id="element-21249" />}
              <span className="font-bold text-white" data-id="element-21250">{skinData.price}</span>
              <span className="text-gray-400 ml-1" data-id="element-21251">
                {skinData.currency_type}
              </span>
            </div>
            <button onClick={handleUnlock} disabled={!hasEnoughCurrency} className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${hasEnoughCurrency ? 'bg-gradient-to-r from-amber-600 to-red-600 hover:shadow-lg hover:shadow-amber-500/20' : 'bg-gray-700 cursor-not-allowed'} transition-all flex items-center`} data-id="element-21252">
              {hasEnoughCurrency ? <>Unlock Full Skin</> : <>
                  <Lock size={14} className="mr-1" data-id="element-21253" />
                  Insufficient Funds
                </>}
            </button>
          </div>
        </div>
      </div>
    </div>;
};